import { DevicePid } from '@/define/define'
import { DeviceManager as AppDeviceManager } from '@/service/device'
import { DeviceProtocol } from '@/service/device'
import { DriverService } from '@/service/driver/driver'
import { deviceManager as hidDeviceManager } from '@hid/device/device_manager'
import { dataProfileManage } from '@hid/snapshot/profile'
import type { DefineDevice } from '@hid/define/define'
import type { PostDynamicColor } from '@libs/types/response'
import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message'

let connectPromise: Promise<boolean> | null = null

const getCurrentDefineDevice = () => {
  const pid = AppDeviceManager.instance.state.currentPid
  return pid === DevicePid.None ? null : (pid as unknown as DefineDevice)
}

const hasConnectedHidDriver = (deviceID: DefineDevice) => {
  try {
    if (hidDeviceManager.deviceID !== deviceID) {
      return false
    }
    void hidDeviceManager.deviceDriver
    return true
  } catch {
    return false
  }
}

const refreshAuthorizedWebDevices = async () => {
  const devices = await hidDeviceManager.webDevices()
  AppDeviceManager.instance.state.webDeviceList = hidDeviceManager.respOfDevices(devices)
}

const connectCurrentHidDevice = async (requestAccess: boolean) => {
  const deviceID = getCurrentDefineDevice()
  if (!deviceID) {
    return false
  }

  if (hasConnectedHidDriver(deviceID)) {
    return true
  }

  const connect = async () => {
    await hidDeviceManager.connect(deviceID)
    dataProfileManage().reload()
    return true
  }

  try {
    await refreshAuthorizedWebDevices()
    return await connect()
  } catch (error) {
    if (!requestAccess) {
      console.warn('dynamic light hid connect failed', error)
      return false
    }
  }

  try {
    const devices = await hidDeviceManager.request()
    AppDeviceManager.instance.state.webDeviceList = hidDeviceManager.respOfDevices(devices)
    return await connect()
  } catch (error) {
    console.warn('dynamic light hid authorization failed', error)
    return false
  }
}

export const ensureHidLightDeviceConnected = async (options: { requestAccess?: boolean } = {}) => {
  if (connectPromise) {
    return connectPromise
  }

  connectPromise = connectCurrentHidDevice(Boolean(options.requestAccess)).finally(() => {
    connectPromise = null
  })
  return connectPromise
}

export const dynamicLightFrameWriter = {
  async setDynamicState(colorsDynamic: PostDynamicColor, options: { requestAccess?: boolean } = {}) {
    if (AppDeviceManager.instance.state.driverProtocol === DeviceProtocol.dll_exe) {
      try {
        return await DriverService.instance.askTypeAndData<boolean>({
          Type: DriverSocketMsgType.MTMusicalRhythm,
          Data: colorsDynamic,
        } as DriverSocketMsg<PostDynamicColor>)
      } catch (error) {
        console.warn('dynamic light socket state write failed', error)
        return false
      }
    }

    if (!(await ensureHidLightDeviceConnected({ requestAccess: options.requestAccess }))) {
      return false
    }

    try {
      const profile = dataProfileManage().dataProfile
      profile.ColorsDynamic = { ...colorsDynamic }
      profile.LightLastIsCustom = false
      dataProfileManage().save()
      return true
    } catch (error) {
      console.warn('dynamic light state write failed', error)
      return false
    }
  },

  async sendFrame(frame: number[]) {
    if (AppDeviceManager.instance.state.driverProtocol === DeviceProtocol.dll_exe) {
      try {
        return await DriverService.instance.askTypeAndData<boolean>({
          Type: DriverSocketMsgType.MTImageLightFrame,
          Data: frame,
        } as DriverSocketMsg<number[]>)
      } catch (error) {
        console.warn('dynamic light socket frame write failed', error)
        return false
      }
    }

    if (!(await ensureHidLightDeviceConnected())) {
      return false
    }

    try {
      return await hidDeviceManager.deviceDriver.syncImageLightFrame(frame)
    } catch (error) {
      console.warn('dynamic light frame write failed', error)
      return false
    }
  },
}
